/**
 * API keys service — tenant-scoped developer keys from shared-auth
 * (ADR-007). Keys authenticate the SystemDox MCP server and other
 * API clients via the dual-path authoriser.
 *
 * The full plaintext key is returned ONLY by create(); afterwards the
 * server holds an Argon2id hash and can surface just the last4.
 * Management (mint/rename/revoke) requires the owner or admin role;
 * listing requires membership only.
 */

import axios from 'axios';
import { AUTH_API_URL } from '@/lib/apiConfig';
import type { KeyMode } from '@/lib/apiKeyAccess';
import type { TenantBinding } from '@/services/tenantApi';

const api = axios.create({ baseURL: AUTH_API_URL });

export interface ApiKeyMetadata {
  keyId: string;
  name: string;
  services: string[];
  /** The space(s) the key reaches and the preset scopes there; none on a key minted before 2026-10-27. */
  bindings?: TenantBinding[];
  /** `write` keeps the bindings' write scopes; `read` (the server default) strips them. */
  mode?: KeyMode;
  last4: string;
  createdAt: string;
  lastUsedAt?: string;
  revokedAt?: string;
}

export interface CreatedApiKey extends ApiKeyMetadata {
  /** Full plaintext key — shown once, unrecoverable afterwards. */
  key: string;
}

export const apiKeysApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /** List the tenant's keys (metadata only — never the plaintext). */
  async list(): Promise<ApiKeyMetadata[]> {
    const { data } = await api.get('/api-keys');
    return data.items ?? [];
  },

  /**
   * Mint a new key with what it may do (lib/apiKeyAccess › keyAccessRequest).
   * The response is the only time the plaintext exists client-side.
   */
  async create(name: string, access: { bindings: TenantBinding[]; mode: KeyMode }): Promise<CreatedApiKey> {
    const { data } = await api.post('/api-keys', { name, services: [], ...access });
    return data;
  },

  /** Rename a key (owner/admin only). */
  async rename(keyId: string, name: string): Promise<ApiKeyMetadata> {
    const { data } = await api.patch(`/api-keys/${keyId}`, { name });
    return data;
  },

  /** Revoke a key — soft delete; the key stops authenticating immediately. */
  async revoke(keyId: string): Promise<void> {
    await api.delete(`/api-keys/${keyId}`);
  },
};
